/**
 * Verified A-vs-B snapshot facts from Product Source of Truth data files.
 * Does not import recommendation, runtime, compatibility, or role engines.
 * Does not read legacy src/data catalogs.
 */

import canonicalUniverse from '../../domain/canonicalUniverse.json';
import weightSpecFile from '../../domain/productWeightSpecs.json';
import liveScopeSpecFile from '../../domain/productLiveScopeSpecs.json';
import packageSpecFile from '../../domain/productHdPackageSpecs.json';

const LIVESCOPE_SPEC_KEYS = {
  MAX_FORWARD_DOWN_DISTANCE_FT: 'max_forward_down_distance_ft',
  TARGET_SEPARATION_30FT_IN: 'target_separation_30ft_in',
  TARGET_SEPARATION_100FT_IN: 'target_separation_100ft_in',
  FREQUENCY_RANGE: 'frequency_range',
  DIMENSIONS_LXHXW: 'dimensions_lxhxw',
  FORWARD_BEAMWIDTH: 'forward_beamwidth',
  DOWN_PERSPECTIVE_BEAMWIDTH: 'down_perspective_beamwidth',
  DIRECT_CONNECT: 'direct_connect',
  OPERATING_TEMPERATURE_F: 'operating_temperature_f',
};

function familyBySlug(slug) {
  return (canonicalUniverse.hardwareFamilies || []).find((row) => row.slug === slug) || null;
}

export function getCanonicalProductRecord(slug) {
  if (!slug) return null;
  return (canonicalUniverse.products || []).find((row) => row.slug === slug) || null;
}

export function getVerifiedUnitWeightSpec(slug) {
  if (!slug) return null;
  return (weightSpecFile.specs || []).find((row) => row.productSlug === slug) || null;
}

export function getVerifiedUnitWeightPounds(slug) {
  const pounds = Number(getVerifiedUnitWeightSpec(slug)?.specValueNumeric);
  return Number.isFinite(pounds) && pounds > 0 ? pounds : null;
}

export function getVerifiedUnitWeightLabel(slug) {
  const spec = getVerifiedUnitWeightSpec(slug);
  if (!spec) return null;
  if (spec.displayLabel) return spec.displayLabel;
  const pounds = getVerifiedUnitWeightPounds(slug);
  if (pounds == null) return null;
  return `${pounds} lb`;
}

export function getLiveScopeBeamSweepDeg(slug, specKey) {
  const spec = getLiveScopeSpecRecord(slug, specKey);
  if (!spec) return null;
  const numeric = Number(spec.specValueNumeric);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  const text = spec.displayLabel || spec.specValueText || '';
  const values = [...text.matchAll(/(\d+(?:\.\d+)?)\s*°/g)].map((row) => Number(row[1]));
  if (!values.length) return null;
  return Math.max(...values);
}

function formatPoundsLabel(pounds, digits) {
  if (pounds == null || !Number.isFinite(pounds)) return null;
  return `${pounds.toFixed(digits)} lb`;
}

export function getUpgradeComparisonFacts(ownedSlug, consideredSlug) {
  const ownedWeightLb = getVerifiedUnitWeightPounds(ownedSlug);
  const glsWeightLb = getVerifiedUnitWeightPounds('garmin-gls-10');
  const consideredWeightLb = getVerifiedUnitWeightPounds(consideredSlug);
  const publishedComponentLb =
    ownedWeightLb != null && glsWeightLb != null ? ownedWeightLb + glsWeightLb : null;
  const publishedDiffLb =
    publishedComponentLb != null && consideredWeightLb != null
      ? publishedComponentLb - consideredWeightLb
      : null;
  const ownedDirect = getLiveScopeSpecRecord(ownedSlug, LIVESCOPE_SPEC_KEYS.DIRECT_CONNECT);
  const consideredDirect = getLiveScopeSpecRecord(
    consideredSlug,
    LIVESCOPE_SPEC_KEYS.DIRECT_CONNECT
  );

  return {
    ownedMaxFt: getLiveScopeSpecNumeric(ownedSlug, LIVESCOPE_SPEC_KEYS.MAX_FORWARD_DOWN_DISTANCE_FT),
    consideredMaxFt: getLiveScopeSpecNumeric(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.MAX_FORWARD_DOWN_DISTANCE_FT
    ),
    ownedSep30: getLiveScopeSpecLabel(ownedSlug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN),
    consideredSep30: getLiveScopeSpecLabel(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN
    ),
    ownedSep100: getLiveScopeSpecLabel(ownedSlug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN),
    consideredSep100: getLiveScopeSpecLabel(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN
    ),
    ownedSep100In: getLiveScopeSpecNumeric(
      ownedSlug,
      LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN
    ),
    consideredSep100In: getLiveScopeSpecNumeric(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN
    ),
    ownedFrequency: getLiveScopeSpecLabel(ownedSlug, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE),
    consideredFrequency: getLiveScopeSpecLabel(consideredSlug, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE),
    ownedForwardSweep: getLiveScopeBeamSweepDeg(ownedSlug, LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH),
    consideredForwardSweep: getLiveScopeBeamSweepDeg(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH
    ),
    ownedDownSweep: getLiveScopeBeamSweepDeg(
      ownedSlug,
      LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH
    ),
    consideredDownSweep: getLiveScopeBeamSweepDeg(
      consideredSlug,
      LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH
    ),
    ownedRequiresGls: ownedDirect?.specValueBoolean === false,
    consideredRequiresGls: consideredDirect?.specValueBoolean === true ? false : null,
    ownedWeightLabel: getVerifiedUnitWeightLabel(ownedSlug),
    consideredWeightLabel: getVerifiedUnitWeightLabel(consideredSlug),
    glsWeightLabel: getVerifiedUnitWeightLabel('garmin-gls-10'),
    iceWeightLabel: getVerifiedUnitWeightLabel('livescope-2-lvs42hd-ice'),
    ownedWeightLb,
    glsWeightLb,
    consideredWeightLb,
    publishedComponentLb,
    publishedComponentLabel: formatPoundsLabel(publishedComponentLb, 2),
    publishedDiffLb,
    approxPublishedDiffLabel:
      publishedDiffLb == null ? null : `approximately ${formatPoundsLabel(Math.round(publishedDiffLb * 10) / 10, 1)}`,
  };
}

export function buildUpgradeSpecRows(ownedSlug, consideredSlug) {
  const facts = getUpgradeComparisonFacts(ownedSlug, consideredSlug);
  const rows = [
    {
      id: 'max-range',
      label: 'Max range',
      a: facts.ownedMaxFt != null ? `${facts.ownedMaxFt} ft` : null,
      b: facts.consideredMaxFt != null ? `${facts.consideredMaxFt} ft` : null,
    },
    {
      id: 'sep-30',
      label: 'Target separation at 30 ft',
      a: facts.ownedSep30 || 'Not published',
      b: facts.consideredSep30,
    },
    {
      id: 'sep-100',
      label: 'Target separation at 100 ft',
      a: facts.ownedSep100,
      b: facts.consideredSep100,
    },
    {
      id: 'frequency',
      label: 'Frequency',
      a: facts.ownedFrequency,
      b: facts.consideredFrequency,
    },
    {
      id: 'forward-beam',
      label: 'Forward field of view',
      a: facts.ownedForwardSweep != null ? `${facts.ownedForwardSweep}°` : null,
      b: facts.consideredForwardSweep != null ? `${facts.consideredForwardSweep}°` : null,
    },
    {
      id: 'down-beam',
      label: 'Down / Perspective field of view',
      a: facts.ownedDownSweep != null ? `${facts.ownedDownSweep}°` : null,
      b: facts.consideredDownSweep != null ? `${facts.consideredDownSweep}°` : null,
    },
    {
      id: 'gls-10',
      label: 'GLS 10',
      a: facts.ownedRequiresGls ? 'Required' : null,
      b: facts.consideredRequiresGls === false ? 'Not required' : null,
    },
    {
      id: 'xdcr-weight',
      label: 'Published transducer weight',
      a: facts.ownedWeightLabel ? `${facts.ownedWeightLabel}, transducer only` : null,
      b: facts.consideredWeightLabel ? `${facts.consideredWeightLabel}, includes cable` : null,
    },
    {
      id: 'gls-weight',
      label: 'GLS 10 weight',
      a: facts.glsWeightLabel ? `${facts.glsWeightLabel} module` : null,
      b: facts.consideredRequiresGls === false ? 'Not in path' : null,
    },
  ];
  return rows.filter((row) => row.a || row.b);
}

export function getLiveScopeGenerationLabel(product) {
  if (!product) return null;
  const family = familyBySlug(product.hardwareFamilySlug);
  if (product.hardwareFamilySlug === 'garmin-livescope-2-hd') return 'LiveScope 2 HD';
  if (product.hardwareFamilySlug === 'garmin-livescope-2') return 'LiveScope 2';
  if (family?.name) return family.name;
  return null;
}

export function formatSystemRoles(product) {
  const roles = Array.isArray(product?.roles) ? product.roles : [];
  if (!roles.length) return null;
  if (roles.includes('SONAR_PROCESSOR_INTERFACE')) {
    return 'Direct-connect live sonar';
  }
  if (roles.includes('LIVE_SONAR')) {
    return 'Live sonar';
  }
  return null;
}

export function getProcessorRequirementLabel(product) {
  const roles = Array.isArray(product?.roles) ? product.roles : [];
  if (roles.includes('SONAR_PROCESSOR_INTERFACE')) {
    return 'GLS 10 not required (direct-connect)';
  }
  if (roles.includes('LIVE_SONAR')) {
    return 'GLS 10 required';
  }
  return null;
}

export function getManufacturerConditionNote() {
  return liveScopeSpecFile.conditionNote || null;
}

export function getLiveScopeSpecRecord(slug, specKey) {
  if (!slug || !specKey) return null;
  return (
    (liveScopeSpecFile.specs || []).find(
      (row) => row.productSlug === slug && row.specKey === specKey
    ) || null
  );
}

export function getLiveScopeSpecLabel(slug, specKey) {
  const spec = getLiveScopeSpecRecord(slug, specKey);
  if (!spec) return null;
  return spec.displayLabel || spec.specValueText || null;
}

export function getPackageSpecRecord(slug, specKey) {
  if (!slug || !specKey) return null;
  return (
    (packageSpecFile.specs || []).find(
      (row) => row.productSlug === slug && row.specKey === specKey
    ) || null
  );
}

export function getPackageSpecLabel(slug, specKey) {
  const spec = getPackageSpecRecord(slug, specKey);
  if (!spec) return null;
  return spec.displayLabel || spec.specValueText || null;
}

export function getHdFamilyMaxDistanceLabel(slug) {
  if (slug === 'livescope-2-lvs42hd-ice') {
    return 'Same HD path as LVS42HD';
  }
  return getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.MAX_FORWARD_DOWN_DISTANCE_FT);
}

export function getHdVariantFacts(slug) {
  return {
    cableLength: getPackageSpecLabel(slug, 'transducer_cable_length_ft'),
    cableType: getPackageSpecLabel(slug, 'transducer_cable_type'),
    mounts: getPackageSpecLabel(slug, 'included_mounts'),
    weight: getVerifiedUnitWeightLabel(slug),
    dimensions:
      getPackageSpecLabel(slug, 'dimensions_lxhxw') ||
      getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DIMENSIONS_LXHXW),
  };
}

export function getDecisionFacts(slug) {
  return {
    maxDistance: getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.MAX_FORWARD_DOWN_DISTANCE_FT),
    targetSeparation30: getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN),
    targetSeparation100: getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN),
    weight: getVerifiedUnitWeightLabel(slug),
    frequency: getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE),
  };
}

function pairRow(id, label, slugA, slugB, getter, note) {
  const a = getter(slugA);
  const b = getter(slugB);
  if (!a && !b) return null;
  return { id, label, a: a || '-', b: b || '-', note: note || null };
}

/**
 * Customer-facing comparison rows that actually help choose.
 * Identical catalog/database fields are omitted.
 */
export function buildNumbersThatMatterRows(slugA, slugB) {
  return [
    pairRow(
      'max-distance',
      'Maximum forward/down distance',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.MAX_FORWARD_DOWN_DISTANCE_FT)
    ),
    pairRow(
      'sep-30',
      'Target separation at 30 ft',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN),
      'Lower is better: it means the sonar can distinguish targets that are closer together.'
    ),
    pairRow(
      'sep-100',
      'Target separation at 100 ft',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN)
    ),
    pairRow('weight', 'Weight including cable', slugA, slugB, getVerifiedUnitWeightLabel),
    pairRow(
      'frequency',
      'Frequency range',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE)
    ),
  ].filter(Boolean);
}

export function getSameOnBothFacts(slugA, slugB) {
  const forwardA = getLiveScopeSpecLabel(slugA, LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH);
  const forwardB = getLiveScopeSpecLabel(slugB, LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH);
  const downA = getLiveScopeSpecLabel(slugA, LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH);
  const downB = getLiveScopeSpecLabel(slugB, LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH);
  const items = [
    'direct connection to a compatible Garmin display and power',
    'no GLS 10 / separate black box',
  ];
  if (forwardA && forwardA === forwardB) {
    items.push(`same ${forwardA} forward beamwidth`);
  }
  if (downA && downA === downB) {
    items.push(`same ${downA} down/perspective beamwidth`);
  }
  return items;
}

export function buildFullTechnicalSpecRows(slugA, slugB) {
  const rows = [
    ...buildNumbersThatMatterRows(slugA, slugB),
    pairRow(
      'dimensions',
      'Dimensions (L × H × W)',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DIMENSIONS_LXHXW)
    ),
    pairRow(
      'forward-beam',
      'Forward beamwidth',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH)
    ),
    pairRow(
      'down-beam',
      'Down/perspective beamwidth',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH)
    ),
    pairRow(
      'connection',
      'Connection',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DIRECT_CONNECT)
    ),
    pairRow(
      'operating-temp',
      'Operating temperature',
      slugA,
      slugB,
      (slug) => getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.OPERATING_TEMPERATURE_F)
    ),
  ].filter(Boolean);

  return rows;
}

function tripleRow(id, label, slugs, getter, note) {
  const values = slugs.map((slug) => getter(slug) || '-');
  if (values.every((value) => value === '-')) return null;
  return { id, label, values, note: note || null };
}

export function buildThreeModelSpecRows(slugHd, slugIce, slugLvs44) {
  const slugs = [slugHd, slugIce, slugLvs44];
  return [
    tripleRow('max-distance', 'Maximum forward/down distance', slugs, getHdFamilyMaxDistanceLabel),
    tripleRow(
      'sep-30',
      'Target separation at 30 ft',
      slugs,
      (slug) =>
        slug === slugIce
          ? getLiveScopeSpecLabel(slugHd, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN)
          : getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_30FT_IN),
      'Lower is better: it means the sonar can distinguish targets that are closer together.'
    ),
    tripleRow(
      'sep-100',
      'Target separation at 100 ft',
      slugs,
      (slug) =>
        slug === slugIce
          ? getLiveScopeSpecLabel(slugHd, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN)
          : getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.TARGET_SEPARATION_100FT_IN)
    ),
    tripleRow('frequency', 'Frequency range', slugs, (slug) =>
      slug === slugIce
        ? getLiveScopeSpecLabel(slugHd, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE)
        : getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.FREQUENCY_RANGE)
    ),
    tripleRow('weight', 'Weight including cable', slugs, getVerifiedUnitWeightLabel),
    tripleRow('cable-length', 'Transducer cable length', slugs, (slug) =>
      getPackageSpecLabel(slug, 'transducer_cable_length_ft')
    ),
    tripleRow('cable-type', 'Cable type', slugs, (slug) =>
      getPackageSpecLabel(slug, 'transducer_cable_type')
    ),
    tripleRow('dimensions', 'Body dimensions (L × H × W)', slugs, (slug) =>
      getPackageSpecLabel(slug, 'dimensions_lxhxw') ||
      getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DIMENSIONS_LXHXW)
    ),
    tripleRow('forward-beam', 'Forward beamwidth', slugs, () =>
      getLiveScopeSpecLabel(slugHd, LIVESCOPE_SPEC_KEYS.FORWARD_BEAMWIDTH)
    ),
    tripleRow('down-beam', 'Down/perspective beamwidth', slugs, () =>
      getLiveScopeSpecLabel(slugHd, LIVESCOPE_SPEC_KEYS.DOWN_PERSPECTIVE_BEAMWIDTH)
    ),
    tripleRow('mounts', 'Included mounts', slugs, (slug) =>
      getPackageSpecLabel(slug, 'included_mounts')
    ),
    tripleRow(
      'connection',
      'Connection',
      slugs,
      (slug) =>
        getPackageSpecLabel(slug, 'direct_connect') ||
        getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.DIRECT_CONNECT)
    ),
    tripleRow('operating-temp', 'Operating temperature', slugs, (slug) =>
      slug === slugIce
        ? null
        : getLiveScopeSpecLabel(slug, LIVESCOPE_SPEC_KEYS.OPERATING_TEMPERATURE_F)
    ),
  ].filter(Boolean);
}

export function getLiveScopeSpecNumeric(slug, specKey) {
  const spec = getLiveScopeSpecRecord(slug, specKey);
  const value = spec == null ? NaN : Number(spec.specValueNumeric);
  return Number.isFinite(value) ? value : null;
}

export function getUpgradeComparisonSourceLinks(options = {}) {
  const liveSources = liveScopeSpecFile.sources || {};
  return [
    {
      id: 'lvs34-install',
      label: 'Garmin LiveScope Plus LVS34 specifications',
      href: liveSources.lvs34InstallSpecs,
    },
    {
      id: 'gls10',
      label: 'Garmin GLS 10 sonar module specifications',
      href: liveSources.gls10Specs,
    },
    {
      id: 'lvs34-press',
      label: 'Garmin LiveScope Plus launch announcement',
      href: liveSources.lvs34PressRelease,
    },
    {
      id: 'lvs42hd-install',
      label: 'Garmin LiveScope 2 installation specifications',
      href: liveSources.installSpecs,
    },
    {
      id: 'lvs42hd',
      label: 'Garmin LVS42HD product page',
      href: liveSources.lvs42hdProductPage,
    },
    {
      id: 'lvs42hd-press',
      label: 'Garmin LiveScope 2 launch announcement',
      href: liveSources.pressRelease,
    },
    options.includeLvs44
      ? {
          id: 'lvs44',
          label: 'Garmin LVS44 product page',
          href: liveSources.lvs44ProductPage,
        }
      : null,
  ].filter((row) => row && row.href);
}

export function getComparisonSourceLinks() {
  const liveSources = liveScopeSpecFile.sources || {};
  const packageSources = packageSpecFile.sources || {};
  return [
    { id: 'install', label: 'Garmin LiveScope 2 installation specifications', href: liveSources.installSpecs || packageSources.installSpecs },
    { id: 'ice-install', label: 'Garmin LVS42HD-IF ice-kit installation specifications', href: packageSources.iceKitInstallSpecs },
    { id: 'lvs42hd', label: 'Garmin LVS42HD product page', href: packageSources.lvs42hdProductPage || liveSources.lvs42hdProductPage },
    { id: 'lvs42hd-if', label: 'Garmin LVS42HD-IF product page', href: packageSources.lvs42hdIfProductPage },
    { id: 'lvs44', label: 'Garmin LVS44 product page', href: packageSources.lvs44ProductPage || liveSources.lvs44ProductPage },
    { id: 'press', label: 'Garmin LiveScope 2 launch announcement', href: liveSources.pressRelease || packageSources.pressRelease },
  ].filter((row) => row.href);
}

/**
 * @deprecated Prefer buildNumbersThatMatterRows / buildFullTechnicalSpecRows.
 * Kept as a thin adapter so existing helpers still have a snapshot builder.
 */
export function buildVerifiedSpecRows(slugA, slugB) {
  return buildNumbersThatMatterRows(slugA, slugB);
}

export { LIVESCOPE_SPEC_KEYS };
